import { useEffect, useMemo, useState } from 'react';
import { Dayjs } from 'dayjs';
import { Download, FileSpreadsheet, FileType } from 'lucide-react';
import { Button, DatePicker, Modal, Segmented, Tag, message } from '../../ui';
import { disableFuture } from '../../lib/dateRange';
import { api } from '../../api/client';

type Format = 'xlsx' | 'pdf';

/** One labelled block in the dialog — label above, control below, optional hint under it. */
function XField({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1.5 type-label-strong text-content">{label}</div>
      {children}
      {hint && <div className="mt-1 text-xs text-muted">{hint}</div>}
    </div>
  );
}

export interface BankExportModalProps {
  open: boolean;
  onClose: () => void;
  /** Every account on screen, in the order shown: `{ key, name, sub? }`. */
  banks: { key: string; name: string; sub?: string | null }[];
  /** The page's live filters (mode / retailer), carried into the file so it matches the view. */
  baseParams: Record<string, any>;
  /** The page's date range, used to seed the dialog's period. */
  initialRange?: [Dayjs, Dayjs] | null;
  /** True while the bank list is still loading — an empty list then means "not yet", not "none". */
  loading?: boolean;
}

/**
 * The "Export summary" dialog.
 *
 * A one-click export always produced the same thing, so anyone wanting a single account or a specific
 * month had to change the page filters first and then export — and had no way to tell, afterwards,
 * which filters a downloaded file had been built under. Here the choices are explicit and land in the
 * file's own header. Every account starts selected: the common case is "the whole picture", and the
 * dialog should not make that the effortful path.
 */
export function BankExportModal({ open, onClose, banks, baseParams, initialRange, loading }: BankExportModalProps) {
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(initialRange ?? null);
  const [format, setFormat] = useState<Format>('xlsx');
  const [detail, setDetail] = useState(true);
  // null = "all", which keeps the selection correct when the bank list itself changes underneath.
  const [picked, setPicked] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setRange(initialRange ?? null); setPicked(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const keys = useMemo(() => banks.map((b) => b.key), [banks]);
  const selected = picked ?? keys;
  const allChecked = selected.length === keys.length && keys.length > 0;
  const toggle = (k: string) =>
    setPicked(selected.includes(k) ? selected.filter((x) => x !== k) : [...selected, k]);

  const run = async () => {
    setBusy(true);
    try {
      const p = new URLSearchParams(baseParams as any);
      if (range?.[0] && range?.[1]) {
        p.set('from', range[0].format('YYYY-MM-DD'));
        p.set('to', range[1].format('YYYY-MM-DD'));
      } else { p.delete('from'); p.delete('to'); }
      // Only send ids when it's a real subset — "everything" is the server's default and keeps the URL clean.
      if (!allChecked) p.set('bankAccountIds', selected.join(','));
      if (!detail) p.set('detail', '0');

      const res = await api.get(`/payments/bank-summary.${format}?${p.toString()}`, { responseType: 'blob' });
      const mime = format === 'pdf'
        ? 'application/pdf'
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const period = range?.[0] && range?.[1] ? `_${range[0].format('YYYY-MM-DD')}_to_${range[1].format('YYYY-MM-DD')}` : '';
      const who = allChecked ? 'all-banks' : `${selected.length}-banks`;
      const url = URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `bank-summary_${who}${period}.${format}`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 100);
      message.success('Export downloaded');
      onClose();
    } catch (e: any) {
      // The error body arrives as a Blob because the request asked for one — read it, don't discard
      // the server's reason and replace it with a shrug.
      let msg = 'Could not export. Try a narrower period or fewer accounts.';
      const body = e?.response?.data;
      if (body instanceof Blob) {
        try { msg = JSON.parse(await body.text())?.error?.message ?? msg; } catch { /* keep default */ }
      } else if (body?.error?.message) { msg = body.error.message; }
      message.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={(o: boolean) => { if (!o) onClose(); }}
      title="Export bank collections"
      description="Choose the period, which accounts to include, and the format."
      width={620}
      footer={
        <>
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={busy} disabled={!selected.length} icon={<Download size={16} />} onClick={run}>
            Export {format === 'pdf' ? 'PDF' : 'Excel'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <XField label="Period" hint={range ? undefined : 'Leave empty for all time.'}>
          <DatePicker.RangePicker
            disabledDate={disableFuture}
            value={range as any}
            onChange={(v) => setRange(v as [Dayjs, Dayjs] | null)}
          />
        </XField>

        <XField label="Format">
          <Segmented
            value={format}
            onChange={(v) => setFormat(v as Format)}
            options={[
              { label: 'Excel', value: 'xlsx', icon: <FileSpreadsheet /> },
              { label: 'PDF', value: 'pdf', icon: <FileType /> },
            ]}
          />
        </XField>

        <XField
          label="How much detail"
          hint={detail
            ? (format === 'xlsx'
              ? 'A Summary sheet with the per-account totals, plus a Transactions sheet holding every entry behind them.'
              : 'The totals and the per-account table, then a full register for each account.')
            : 'The per-account totals only — no individual transactions.'}
        >
          <Segmented
            value={detail ? 'full' : 'summary'}
            onChange={(v) => setDetail(v === 'full')}
            options={[
              { label: 'Summary only', value: 'summary' },
              { label: 'Full — every transaction', value: 'full' },
            ]}
          />
        </XField>

        <XField label={`Bank accounts (${selected.length} of ${keys.length} selected)`}>
          {loading ? (
            <div className="rounded-control bg-surface-2 px-3 py-2 text-sm text-muted">Loading accounts…</div>
          ) : !keys.length ? (
            <div className="rounded-control bg-surface-2 px-3 py-2 text-sm text-muted">
              No accounts have any activity for these filters.
            </div>
          ) : (
            <div className="rounded-control border border-line">
              <label className="flex cursor-pointer items-center gap-2 border-b border-line px-3 py-2 type-body-medium">
                <input
                  type="checkbox"
                  checked={allChecked}
                  ref={(el) => { if (el) el.indeterminate = !allChecked && selected.length > 0; }}
                  onChange={() => setPicked(allChecked ? [] : null)}
                />
                Select all
              </label>
              <div className="max-h-56 overflow-auto">
                {banks.map((b) => (
                  <label key={b.key} className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm hover:bg-surface-2">
                    <input type="checkbox" checked={selected.includes(b.key)} onChange={() => toggle(b.key)} />
                    <span className="truncate">{b.name}</span>
                    {b.key === 'NONE' && <Tag tone="neutral" style={{ margin: 0 }}>Cash</Tag>}
                  </label>
                ))}
              </div>
            </div>
          )}
        </XField>
      </div>
    </Modal>
  );
}
